import Cookies from 'js-cookie';
import axios from 'axios';
import log from './logService';

const ACCESS_TOKEN_COOKIE = process.env.REACT_APP_ACCESS_TOKEN_COOKIE;
const REFRESH_TOKEN_COOKIE = process.env.REACT_APP_REFRESH_TOKEN_COOKIE;

export const fetchWithAuth = async (url, options = {}) => {
  log('fetchWithAuth:', url, options);

  let accessToken = Cookies.get(ACCESS_TOKEN_COOKIE);
  log('accessToken do cookie:', accessToken);
  
  if (!accessToken) {
    log('Token não encontrado no cookie, tentando renovar...');
    accessToken = await renewAccessToken();
    log('accessToken Renovado:', accessToken);
  }

  if (accessToken) {
    try {
      const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));
      const expiryTime = tokenPayload.exp * 1000;
      log('expiryTime:', expiryTime);
      log('Tempo atual:', Date.now());
      if (Date.now() >= expiryTime) {
        log('Token expirado, renovando...');
        accessToken = await renewAccessToken();
        log('accessToken Renovado após expiração:', accessToken);
      }
    } catch (error) {
      log('Erro ao decodificar token:', error);
      accessToken = await renewAccessToken();
    }
  }

  if (!accessToken) {
    log('Não foi possível obter um token válido');
    throw new Error("Não foi possível renovar o token de acesso.");
  }

  // Garantir que o token seja enviado com o prefixo Bearer
  const token = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
  
  const headers = {
    ...options.headers,
    'Authorization': token,
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  
  log('headers completos:', JSON.stringify(headers, null, 2));
  
  const fetchOptions = {
    ...options,
    headers,
    credentials: 'include',
    mode: 'cors',
    cache: 'no-cache',
    redirect: 'follow',
    referrerPolicy: 'no-referrer-when-downgrade',
    keepalive: true
  };
  
  log('fetchOptions:', JSON.stringify(fetchOptions, null, 2));
  
  try {
    log('Iniciando requisição para:', url);
    log('Headers enviados:', JSON.stringify(headers, null, 2));
    let response = await fetch(url, fetchOptions);
    
    log('Resposta recebida:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
      url: response.url
    });
    
    if (response.status === 401) {
      log('Recebido 401, tentando renovar token...');
      // Token expirado, tentar renovar
      accessToken = await renewAccessToken();
      log('accessToken Renovado após 401:', accessToken);
      if (accessToken) {
        const newToken = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
        headers.Authorization = newToken;
        response = await fetch(url, { 
          ...options, 
          headers,
          credentials: 'include',
          mode: 'cors',
          cache: 'no-cache',
          redirect: 'follow',
        });
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      log('Erro na resposta da API:', errorText);
      throw new Error(`Erro na resposta da API: ${response.statusText}, ${errorText}, ${response.status}, ${response.url}`);
    }
    return response;
  } catch (error) {
    log('Erro ao fazer a requisição:', error);
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      log('Detalhes do erro CORS:', {
        url,
        headers,
        options: fetchOptions
      });
    }
    throw error;
  }
};

export const renewAccessToken = async () => {
  try {
    const refreshToken = Cookies.get(REFRESH_TOKEN_COOKIE);
    log('Tentando renovar token com refreshToken:', refreshToken);
    log('process.env.REACT_APP_REFRESH_TOKEN_URI:', process.env.REACT_APP_REFRESH_TOKEN_URI);
    log('Body:', JSON.stringify({ refreshToken }));
    
    if (!refreshToken) {
      log('Refresh token não encontrado');
      return null;
    }

    const response = await fetch(process.env.REACT_APP_REFRESH_TOKEN_URI, {
      method: 'POST',
      headers: { 
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({ refreshToken }),
      credentials: 'include',
    });
    
    log('Resposta do refresh token:', response.status);
    const data = await response.json();
    log('Dados do refresh token:', data);

    if (data.success) {
      const accessTokenExpiration = new Date(new Date().getTime() + process.env.REACT_APP_JWT_EXPIRATION * 60 * 1000);
      const refreshTokenExpiration = new Date(new Date().getTime() + process.env.REACT_APP_JWT_REFRESH_EXPIRATION * 24 * 60 * 60 * 1000);
      
      // Configuração dos cookies
      const cookieOptions = {
        expires: accessTokenExpiration,
        secure: true,
        sameSite: 'Lax',
        path: '/'
      };

      Cookies.set(ACCESS_TOKEN_COOKIE, data.accessToken, cookieOptions);
      Cookies.set(REFRESH_TOKEN_COOKIE, data.refreshToken, {
        ...cookieOptions,
        expires: refreshTokenExpiration
      });

      log('Cookies configurados:', {
        accessToken: Cookies.get(ACCESS_TOKEN_COOKIE),
        refreshToken: Cookies.get(REFRESH_TOKEN_COOKIE)
      });

      return data.accessToken;
    } else {
      log('Erro ao renovar token:', data.message);
    }
  } catch (error) {
    log('Erro ao renovar o token de acesso:', error);
  }
  return null;
};

export const logout = () => {
  Cookies.remove(ACCESS_TOKEN_COOKIE, { path: '/' });
  Cookies.remove(REFRESH_TOKEN_COOKIE, { path: '/' });
};

export const registerUser = async (email, password, name) => {
  try {
    const response = await axios.post(process.env.REACT_APP_REGISTER_URI, { email, password, name });
    return response.data;
  } catch (error) {
    log('Erro ao registrar usuário:', error);
    throw error;
  }
};